import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Calendar, Users, MapPin, ArrowLeft, Clock, CheckCircle, 
  GraduationCap, PartyPopper, Wallet, Handshake, Send, X,
  ChevronLeft, ChevronRight, List, CalendarDays, Landmark,
  Music, ExternalLink, Globe, User
} from 'lucide-react';
import { api } from '../services/api';
import Breadcrumbs from '../components/Breadcrumbs';
import BackendImage from '../components/BackendImage';


const tipoConfig = {
  capacitacao: { label: 'Capacitação', icon: GraduationCap, color: 'bg-blue-100 text-blue-700', borderColor: 'border-blue-200' },
  evento: { label: 'Evento', icon: PartyPopper, color: 'bg-purple-100 text-purple-700', borderColor: 'border-purple-200' },
  acao: { label: 'Ação', icon: Users, color: 'bg-teal-100 text-teal-700', borderColor: 'border-teal-200' },
  institucional: { label: 'Institucional', icon: Landmark, color: 'bg-indigo-100 text-indigo-700', borderColor: 'border-indigo-200' },
  cultural: { label: 'Cultural', icon: Music, color: 'bg-pink-100 text-pink-700', borderColor: 'border-pink-200' },
  financeiro: { label: 'Financeiro', icon: Wallet, color: 'bg-green-100 text-green-700', borderColor: 'border-green-200' },
  parceria: { label: 'Parceria', icon: Handshake, color: 'bg-orange-100 text-orange-700', borderColor: 'border-orange-200' },
};

const statusConfig = {
  agendado: { label: 'Agendado', color: 'bg-blue-100 text-blue-700' },
  em_andamento: { label: 'Em Andamento', color: 'bg-green-100 text-green-700' },
  adiado: { label: 'Adiado', color: 'bg-yellow-100 text-yellow-700' },
};

export default function ProgramasAcoes() {
  const navigate = useNavigate();
  const { municipioSlug } = useParams();
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [municipio, setMunicipio] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'calendar'
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    telefone: '',
    negocio_nome: '',
    mensagem: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);

  useEffect(() => {
    loadPrograms();
  }, []);

  // Load municipality info
  useEffect(() => {
    const loadMunicipio = async () => {
      if (municipioSlug) {
        try {
          const res = await api.get('/municipios');
          const found = res.data.find(m => m.slug === municipioSlug);
          if (found) setMunicipio(found);
        } catch (e) {
          console.error('Error loading municipio:', e);
        }
      }
    };
    loadMunicipio();
  }, [municipioSlug]);

  const loadPrograms = async () => {
    try {
      setLoading(true);
      const res = await api.get('/programs');
      setPrograms(res.data);
    } catch (error) {
      console.error('Erro ao carregar programas:', error);
    } finally {
      setLoading(false);
    }
  };

  const openRegistrationModal = (program) => {
    setSelectedProgram(program);
    setFormData({ nome: '', email: '', telefone: '', negocio_nome: '', mensagem: '' });
    setSubmitResult(null);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedProgram) return;

    try {
      setSubmitting(true);
      const res = await api.post(`/programs/${selectedProgram.id}/register`, formData);
      setSubmitResult({ success: true, lista_espera: !!res.data?.lista_espera });
      loadPrograms(); // Refresh to update registration count
    } catch (error) {
      setSubmitResult({ 
        success: false, 
        message: error.response?.data?.detail || 'Erro ao realizar inscrição' 
      });
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const getVagasInfo = (program) => {
    const temLimite = Number.isInteger(program.vagas) && program.vagas > 0;
    const restantes = temLimite ? Math.max(0, program.vagas - (program.inscritos || 0)) : null;
    return {
      temLimite,
      restantes,
      lotado: temLimite && restantes === 0,
      poucasVagas: temLimite && restantes > 0 && restantes <= Math.max(1, Math.ceil(program.vagas * 0.15)),
    };
  };

  const getHorario = (program) => {
    if (program.horario) return program.horario;
    if (program.hora_inicio) return `${program.hora_inicio}${program.hora_fim ? ` às ${program.hora_fim}` : ''}`;
    return null;
  };

  // Calendar helper functions
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    return { daysInMonth, startingDay };
  };

  const getEventsForDay = (day) => {
    const checkDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    return programs.filter(p => {
      const startDate = new Date(p.data_inicio);
      const endDate = p.data_fim ? new Date(p.data_fim) : startDate;
      // Check if day falls within event range
      return checkDate >= new Date(startDate.setHours(0,0,0,0)) && 
             checkDate <= new Date(endDate.setHours(23,59,59,999));
    });
  };

  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const monthNames = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];

  const dayNames = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

  // Calendar View Component
  const CalendarView = () => {
    const { daysInMonth, startingDay } = getDaysInMonth(currentMonth);
    const days = [];
    
    // Empty cells for days before the first day of the month
    for (let i = 0; i < startingDay; i++) {
      days.push(<div key={`empty-${i}`} className="h-24 bg-gray-50"></div>);
    }
    
    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const events = getEventsForDay(day);
      const isToday = new Date().toDateString() === 
        new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day).toDateString();
      
      days.push(
        <div 
          key={day}
          className={`min-h-24 p-1 border-b border-r border-gray-100 ${isToday ? 'bg-purple-50' : 'bg-white'}`}
        >
          <div className={`text-sm font-medium mb-1 ${isToday ? 'text-[var(--primary)]' : 'text-gray-700'}`}>
            {day}
          </div>
          <div className="space-y-1">
            {events.slice(0, 3).map((event, idx) => {
              const config = tipoConfig[event.tipo] || tipoConfig.capacitacao;
              return (
                <button
                  key={event.id}
                  onClick={() => openRegistrationModal(event)}
                  className={`w-full text-left px-1.5 py-0.5 rounded text-xs truncate ${config.color} hover:opacity-80 transition-opacity`}
                  title={event.titulo}
                >
                  {event.titulo}
                </button>
              );
            })}
            {events.length > 3 && (
              <div className="text-xs text-gray-500 pl-1">
                +{events.length - 3} mais
              </div>
            )}
          </div>
        </div>
      );
    }
    
    return (
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        {/* Calendar Header */}
        <div className="flex items-center justify-between p-4 border-b bg-gray-50">
          <button
            onClick={prevMonth}
            className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
            data-testid="prev-month-btn"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h3 className="text-lg font-bold">
            {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
          </h3>
          <button
            onClick={nextMonth}
            className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
            data-testid="next-month-btn"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
        
        {/* Day Headers */}
        <div className="grid grid-cols-7 bg-gray-100">
          {dayNames.map(day => (
            <div key={day} className="py-2 text-center text-sm font-medium text-gray-600">
              {day}
            </div>
          ))}
        </div>
        
        {/* Calendar Grid */}
        <div className="grid grid-cols-7">
          {days}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[var(--background)] pb-20">
      {/* Header with Breadcrumbs */}
      <div className="bg-gradient-to-r from-[#56007F] to-[#7B1FA2] text-white">
        <div className="max-w-6xl mx-auto px-4 py-8">
          <Breadcrumbs 
            items={[
              { label: 'Eventos, Programas e Ações', path: null }
            ]}
            municipio={municipio}
            className="mb-4"
          />
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-4 bg-white/20 rounded-2xl">
                <GraduationCap className="w-10 h-10" />
              </div>
              <div>
                <h1 className="text-3xl font-bold" style={{ fontFamily: 'Manrope' }}>
                  Eventos, Programas e Ações
                </h1>
                <p className="text-white/80 text-lg">
                  Eventos institucionais, culturais, capacitações e oportunidades no município
                </p>
              </div>
            </div>
            
            {/* View Toggle */}
            <div className="flex items-center gap-2 bg-white/20 rounded-xl p-1">
              <button
                onClick={() => setViewMode('list')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                  viewMode === 'list' 
                    ? 'bg-white text-[var(--primary)]' 
                    : 'text-white hover:bg-white/10'
                }`}
                data-testid="view-list-btn"
              >
                <List className="w-4 h-4" />
                <span className="hidden sm:inline">Lista</span>
              </button>
              <button
                onClick={() => setViewMode('calendar')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                  viewMode === 'calendar' 
                    ? 'bg-white text-[var(--primary)]' 
                    : 'text-white hover:bg-white/10'
                }`}
                data-testid="view-calendar-btn"
              >
                <CalendarDays className="w-4 h-4" />
                <span className="hidden sm:inline">Calendário</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {loading ? (
          <div className="text-center py-16">
            <div className="inline-block w-12 h-12 border-4 border-[var(--primary)] border-t-transparent rounded-full animate-spin"></div>
            <p className="mt-4 text-[var(--text-secondary)]">Carregando programas...</p>
          </div>
        ) : programs.length === 0 ? (
          <div className="text-center py-16">
            <GraduationCap className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Nenhum evento disponível</h2>
            <p className="text-[var(--text-secondary)]">
              Em breve teremos novas oportunidades. Fique atento!
            </p>
          </div>
        ) : viewMode === 'calendar' ? (
          // Calendar View
          <CalendarView />
        ) : (
          // List View
          <div className="grid gap-6 md:grid-cols-2">
            {programs.map((program) => {
              const config = tipoConfig[program.tipo] || tipoConfig.evento;
              const Icon = config.icon;
              const tipoInscricao = program.tipo_inscricao || 'interna';
              const { temLimite, restantes, lotado, poucasVagas } = getVagasInfo(program);
              const imgSrc = program.imagens?.medium || program.imagem_url;
              const horarioLabel = getHorario(program);

              return (
                <div
                  key={program.id}
                  className={`bg-white rounded-2xl border-2 ${config.borderColor} overflow-hidden hover:shadow-lg transition-shadow flex flex-col`}
                  data-testid={`program-${program.id}`}
                >
                  {/* Imagem de capa */}
                  {imgSrc && (
                    <div className="h-44 w-full overflow-hidden bg-gray-100">
                      <BackendImage
                        src={imgSrc}
                        alt={program.titulo}
                        className="w-full h-full object-cover"
                        fallback={<div className="w-full h-full bg-gray-100" aria-hidden="true" />}
                      />
                    </div>
                  )}

                  <div className="p-6 flex-1 flex flex-col">
                    {/* Badge de tipo e status */}
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${config.color}`}>
                          <Icon className="w-4 h-4" />
                          {config.label}
                        </span>
                        {program.status === 'adiado' && (
                          <span className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-sm font-medium">
                            Adiado
                          </span>
                        )}
                        {program.status === 'em_andamento' && (
                          <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                            Em Andamento
                          </span>
                        )}
                      </div>
                      {tipoInscricao === 'interna' && lotado && program.exibir_inscritos !== false && (
                        <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-medium whitespace-nowrap">
                          Fila de espera
                        </span>
                      )}
                    </div>

                    {/* Título e descrição */}
                    <h3 className="text-xl font-bold mb-2">{program.titulo}</h3>
                    <p className="text-[var(--text-secondary)] mb-4 line-clamp-2">
                      {program.descricao}
                    </p>

                    {/* Info cards */}
                    <div className="grid grid-cols-2 gap-3 mb-4">
                      <div className="flex items-center gap-2 text-sm text-[var(--text-secondary)]">
                        <Calendar className="w-4 h-4 text-[var(--primary)] flex-shrink-0" />
                        <span>
                          {formatDate(program.data_inicio)}
                          {program.data_fim && program.data_fim !== program.data_inicio && (
                            <> até {formatDate(program.data_fim)}</>
                          )}
                        </span>
                      </div>
                      {horarioLabel && (
                        <div className="flex items-center gap-2 text-sm text-[var(--text-secondary)]">
                          <Clock className="w-4 h-4 text-[var(--primary)] flex-shrink-0" />
                          <span>{horarioLabel}</span>
                        </div>
                      )}
                      {program.online ? (
                        <div className="flex items-center gap-2 text-sm text-[var(--text-secondary)]">
                          <Globe className="w-4 h-4 text-[var(--primary)] flex-shrink-0" />
                          <span>Online</span>
                        </div>
                      ) : program.local ? (
                        <div className="flex items-center gap-2 text-sm text-[var(--text-secondary)]">
                          <MapPin className="w-4 h-4 text-[var(--primary)] flex-shrink-0" />
                          <span className="truncate">{program.local}</span>
                        </div>
                      ) : null}
                      <div className="flex items-center gap-2 text-sm text-[var(--text-secondary)]">
                        <Wallet className="w-4 h-4 text-[var(--primary)] flex-shrink-0" />
                        <span className="font-medium">
                          {program.gratuito !== false
                            ? 'Gratuito'
                            : (program.valor ? `R$ ${Number(program.valor).toFixed(2).replace('.', ',')}` : 'Pago')}
                        </span>
                      </div>
                      {program.organizador && (
                        <div className="flex items-center gap-2 text-sm text-[var(--text-secondary)] col-span-2">
                          <User className="w-4 h-4 text-[var(--primary)] flex-shrink-0" />
                          <span className="truncate">Organização: {program.organizador}</span>
                        </div>
                      )}
                    </div>

                    {/* Vagas — só para inscrição interna e quando o evento permite exibir */}
                    {tipoInscricao === 'interna' && program.exibir_inscritos !== false && (
                      <div className="mb-4">
                        <div className="flex items-center justify-between text-sm mb-1.5">
                          <span className="flex items-center gap-1.5 text-[var(--text-secondary)]">
                            <Users className="w-4 h-4 text-[var(--primary)]" />
                            {temLimite
                              ? `${program.inscritos || 0}/${program.vagas} inscritos`
                              : `${program.inscritos || 0} inscritos`}
                          </span>
                          {temLimite ? (
                            lotado ? (
                              <span className="text-amber-600 font-medium">Vagas esgotadas — fila de espera</span>
                            ) : (
                              <span className={`font-medium ${poucasVagas ? 'text-amber-600' : 'text-green-600'}`}>
                                {poucasVagas ? `Últimas ${restantes} vagas!` : `${restantes} vagas restantes`}
                              </span>
                            )
                          ) : (
                            <span className="text-green-600 font-medium">Vagas ilimitadas</span>
                          )}
                        </div>
                        {temLimite && (
                          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div 
                              className={`h-full transition-all ${lotado ? 'bg-amber-500' : 'bg-[var(--primary)]'}`}
                              style={{ width: `${Math.min(100, ((program.inscritos || 0) / program.vagas) * 100)}%` }}
                            />
                          </div>
                        )}
                      </div>
                    )}

                    {/* Requisitos */}
                    {program.requisitos && (
                      <p className="text-sm text-[var(--text-muted)] mb-4 p-3 bg-gray-50 rounded-lg">
                        <strong>Requisitos:</strong> {program.requisitos}
                      </p>
                    )}

                    {/* Call to action */}
                    <div className="mt-auto">
                      {tipoInscricao === 'externa' && program.link_inscricao_externo ? (
                        <a
                          href={program.link_inscricao_externo}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-full py-3 rounded-xl font-semibold transition-all bg-[var(--primary)] text-white hover:bg-[#46006f] flex items-center justify-center gap-2"
                          data-testid={`external-cta-${program.id}`}
                        >
                          {program.cta_label || 'Saiba mais'}
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      ) : tipoInscricao === 'sem_inscricao' ? null : (
                        <button
                          onClick={() => openRegistrationModal(program)}
                          className={`w-full py-3 rounded-xl font-semibold transition-all active:scale-98 ${
                            lotado && program.exibir_inscritos !== false
                              ? 'bg-amber-500 text-white hover:bg-amber-600'
                              : 'bg-[var(--primary)] text-white hover:bg-[#46006f]'
                          }`}
                          data-testid={`register-btn-${program.id}`}
                        >
                          {lotado && program.exibir_inscritos !== false
                            ? 'Entrar na fila de espera'
                            : (program.cta_label || 'Fazer Inscrição')}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Info Section */}
        <div className="mt-12 p-6 bg-[#56007F]/5 border border-[#56007F]/20 rounded-2xl">
          <h3 className="font-bold text-[#56007F] mb-3 text-lg">Como participar?</h3>
          <div className="grid md:grid-cols-3 gap-4 text-sm text-[var(--text-secondary)]">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-[#56007F] text-white flex items-center justify-center flex-shrink-0">1</div>
              <p>Escolha o programa que mais se adequa ao seu perfil</p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-[#56007F] text-white flex items-center justify-center flex-shrink-0">2</div>
              <p>Preencha o formulário de inscrição com seus dados</p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-[#56007F] text-white flex items-center justify-center flex-shrink-0">3</div>
              <p>Aguarde o contato da equipe com mais informações</p>
            </div>
          </div>
        </div>
      </div>


      {/* Modal de Inscrição */}
      {showModal && selectedProgram && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold">
                  {(selectedProgram.tipo_inscricao || 'interna') === 'interna' ? 'Inscrição' : 'Detalhes do evento'}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-full"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-4 bg-[var(--primary)]/5 rounded-xl mb-6">
                <h3 className="font-semibold text-[var(--primary)]">{selectedProgram.titulo}</h3>
                <p className="text-sm text-[var(--text-secondary)] mt-1">
                  {formatDate(selectedProgram.data_inicio)}
                  {getHorario(selectedProgram) && ` • ${getHorario(selectedProgram)}`}
                  {selectedProgram.online ? ' • Online' : (selectedProgram.local && ` • ${selectedProgram.local}`)}
                </p>
                {selectedProgram.descricao && (
                  <p className="text-sm text-[var(--text-secondary)] mt-2">{selectedProgram.descricao}</p>
                )}
              </div>

              {(selectedProgram.tipo_inscricao === 'externa') ? (
                selectedProgram.link_inscricao_externo ? (
                  <a
                    href={selectedProgram.link_inscricao_externo}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full py-3 bg-[var(--primary)] text-white rounded-xl font-semibold hover:bg-[#46006f] transition-colors flex items-center justify-center gap-2"
                    data-testid="modal-external-cta"
                  >
                    {selectedProgram.cta_label || 'Saiba mais'}
                    <ExternalLink className="w-4 h-4" />
                  </a>
                ) : (
                  <p className="text-center text-sm text-gray-500 py-4">
                    As inscrições deste evento são feitas externamente.
                  </p>
                )
              ) : (selectedProgram.tipo_inscricao === 'sem_inscricao') ? (
                <p className="text-center text-sm text-gray-500 py-4">
                  Este evento não requer inscrição. Basta comparecer!
                </p>
              ) : submitResult?.success ? (
                <div className="text-center py-8">
                  <div className={`w-16 h-16 ${submitResult.lista_espera ? 'bg-amber-100' : 'bg-green-100'} rounded-full flex items-center justify-center mx-auto mb-4`}>
                    {submitResult.lista_espera ? (
                      <Clock className="w-8 h-8 text-amber-600" />
                    ) : (
                      <CheckCircle className="w-8 h-8 text-green-600" />
                    )}
                  </div>
                  {submitResult.lista_espera ? (
                    <>
                      <h3 className="text-xl font-bold text-amber-600 mb-2" data-testid="waitlist-success">Você está na fila de espera!</h3>
                      <p className="text-[var(--text-secondary)] mb-6">
                        As vagas se esgotaram, mas registramos sua inscrição na fila de espera.
                        Caso surjam vagas, você será contatado pelo email informado.
                      </p>
                    </>
                  ) : (
                    <>
                      <h3 className="text-xl font-bold text-green-600 mb-2" data-testid="registration-success">Inscrição Confirmada!</h3>
                      <p className="text-[var(--text-secondary)] mb-6">
                        Você receberá mais informações no email informado.
                      </p>
                    </>
                  )}
                  <button
                    onClick={() => setShowModal(false)}
                    className="px-6 py-3 bg-[var(--primary)] text-white rounded-xl font-medium"
                  >
                    Fechar
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  {getVagasInfo(selectedProgram).lotado && (
                    <div className="p-3 bg-amber-50 border border-amber-200 text-amber-800 rounded-xl text-sm" data-testid="waitlist-notice">
                      <strong>Atenção:</strong> as vagas deste evento se esgotaram. Sua inscrição
                      entrará na <strong>fila de espera</strong> e você será contatado caso surjam vagas.
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium mb-1">Nome completo *</label>
                    <input
                      type="text"
                      required
                      value={formData.nome}
                      onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border border-[var(--border)] focus:ring-2 focus:ring-[var(--primary)]/20 focus:border-[var(--primary)]"
                      placeholder="Seu nome"
                      data-testid="registration-name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Email *</label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border border-[var(--border)] focus:ring-2 focus:ring-[var(--primary)]/20 focus:border-[var(--primary)]"
                      placeholder="seu@email.com"
                      data-testid="registration-email"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Telefone *</label>
                    <input
                      type="tel"
                      required
                      value={formData.telefone}
                      onChange={(e) => setFormData({ ...formData, telefone: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border border-[var(--border)] focus:ring-2 focus:ring-[var(--primary)]/20 focus:border-[var(--primary)]"
                      placeholder="(31) 99999-9999"
                      data-testid="registration-phone"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Nome do seu negócio (opcional)</label>
                    <input
                      type="text"
                      value={formData.negocio_nome}
                      onChange={(e) => setFormData({ ...formData, negocio_nome: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border border-[var(--border)] focus:ring-2 focus:ring-[var(--primary)]/20 focus:border-[var(--primary)]"
                      placeholder="Nome da sua empresa"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Mensagem (opcional)</label>
                    <textarea
                      value={formData.mensagem}
                      onChange={(e) => setFormData({ ...formData, mensagem: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border border-[var(--border)] focus:ring-2 focus:ring-[var(--primary)]/20 focus:border-[var(--primary)] resize-none"
                      rows={3}
                      placeholder="Alguma informação adicional..."
                    />
                  </div>

                  {submitResult?.success === false && (
                    <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">
                      {submitResult.message}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full py-3 bg-[var(--primary)] text-white rounded-xl font-semibold hover:bg-[#46006f] transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                    data-testid="submit-registration"
                  >
                    {submitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Enviando...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        {getVagasInfo(selectedProgram).lotado ? 'Entrar na Fila de Espera' : 'Confirmar Inscrição'}
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
